import { Button } from '@/lib';
import { PerformanceAudienceChartMetric, PerformanceAudienceFilterOption } from '@/services/endpoints/performanceAudience';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PerformanceAudienceDashboardEmpty } from './components';
import { PerformanceDashboardSkeleton } from './components/PerformanceDashboardSkeleton';
import { PerformanceDashboardView } from './components/PerformanceDashboardView';
import { usePerformanceDashboardData } from './hooks/usePerformanceDashboardData';
import { PerformanceAudienceFiltersState } from './types';

type PerformanceDashboardContainerProps = {
  filters: PerformanceAudienceFiltersState;
  filterOptions: {
    audiences: PerformanceAudienceFilterOption[];
    googleScopes: PerformanceAudienceFilterOption[];
    metaScopes: PerformanceAudienceFilterOption[];
  };
  enabled?: boolean;
};

export const PerformanceDashboardContainer: FC<PerformanceDashboardContainerProps> = ({ filters, filterOptions, enabled = true }) => {
  const { t } = useTranslation('performanceAudience');
  const [activeMetric, setActiveMetric] = useState<PerformanceAudienceChartMetric>('impressions');

  const { summary, kpis, trend, comparison, storeVisits, isLoading, isRefetching, isError, refetch, hasData } = usePerformanceDashboardData(
    filters,
    enabled,
    filterOptions
  );

  const isDashboardEmpty = useMemo(() => {
    return !summary && !kpis.length && !trend && !comparison.length && !storeVisits;
  }, [summary, kpis, trend, comparison, storeVisits]);

  const handleExportCsv = useCallback(() => {
    // Placeholder for comparison table CSV export
  }, []);

  const handleEditScope = useCallback(() => {
    // Placeholder for scope editor navigation
  }, []);

  if (isLoading || isRefetching) {
    return <PerformanceDashboardSkeleton />;
  }

  if (isError) {
    return (
      <div className='rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-800'>
        <p>{t('errors.loadFailed')}</p>
        <Button intent='secondary' size='sm' className='mt-4' onClick={() => refetch()}>
          {t('errors.retry')}
        </Button>
      </div>
    );
  }

  if (isDashboardEmpty) {
    return <PerformanceAudienceDashboardEmpty />;
  }

  if (!hasData || !summary || !trend) {
    return null;
  }

  return (
    <PerformanceDashboardView
      summary={summary}
      kpis={kpis}
      trend={trend}
      comparison={comparison}
      storeVisits={storeVisits}
      dateRange={filters.dateRange}
      dateFrom={filters.dateFrom}
      dateTo={filters.dateTo}
      activeMetric={activeMetric}
      onMetricChange={setActiveMetric}
      onEditScope={handleEditScope}
      onExportCsv={handleExportCsv}
    />
  );
};
